'use client';

import { useEffect, useMemo, useState } from 'react';
import type { SelectOption } from '@/app/components/forms/select';
import { MAINTENANCE_LIST_PATH } from '@/app/components/sections/service/maintenance-list/constants';
import type { MaintenanceListSelectsProps, MaintenanceListSelectType } from '@/app/components/sections/service/maintenance-list/selects';
import type { UseMaintenanceList, UseMaintenanceListProps } from '@/app/components/sections/service/maintenance-list/types';
import { getRangeSelectOptionLabel, getRangeSelectOptionValue } from '@/app/components/sections/service/maintenance-list/utils';
import { getCategoryOptionValueAndLabel, getModelOptionValue, getPageUrlFormat, getSortedCategoryOptions, getSortedModelOptions, getYearOptionLabel, getYearOptionValue } from '@/app/components/sections/service/maintenance-list/utils/cv';
import { CATEGORY_TO_NAME, DEFAULT_VEHICLE_IMAGE, MODEL_IMAGE_MAP, SELECTS_DATA } from '@/app/contents/service/maintenance-list/cv';
interface UseMaintenanceListCv extends UseMaintenanceList {
  isEuro5?: boolean;
  getMaintenanceUrl: (selectItem: UseMaintenanceList['selectItem'], selectSchedule: UseMaintenanceList['selectSchedule']) => string;
}
export const useMaintenanceListCv = ({
  url,
  initialSelectValues,
  options
}: UseMaintenanceListProps): UseMaintenanceListCv => {
  const [selectOptionValues, setSelectOptionValues] = useState<MaintenanceListSelectsProps['selectOptionValues']>(initialSelectValues);
  const categorySelectValue = selectOptionValues[0];
  const modelSelectValue = selectOptionValues[1];
  const yearSelectValue = selectOptionValues[2];
  const scheduleSelectValue = selectOptionValues[3];
  const categorySelectOptions = useMemo<SelectOption[]>(() => {
    const sortedOptions = getSortedCategoryOptions(options);
    return sortedOptions.map(item => ({
      label: item.label,
      value: item.key
    }));
  }, [options]);
  const categorySelectItem = useMemo(() => options.find(item => item.key === categorySelectValue), [options, categorySelectValue]);
  const modelList = useMemo(() => {
    return getSortedModelOptions(categorySelectItem?.children || []);
  }, [categorySelectItem]);
  const modelSelectOptions = useMemo<SelectOption[]>(() => {
    if (!categorySelectItem) return [];
    return modelList.map(item => ({
      label: item.label,
      value: item.key
    }));
  }, [categorySelectItem, modelList]);
  const modelSelectItem = useMemo(() => modelList.find(item => item.key === modelSelectValue), [modelList, modelSelectValue]);
  const yearList = useMemo(() => {
    return modelSelectItem?.children || [];
  }, [modelSelectItem]);
  const yearSelectOptions = useMemo<SelectOption[]>(() => {
    if (!categorySelectItem) return [];
    return yearList.map(item => ({
      label: getYearOptionLabel(item),
      value: getYearOptionValue(item)
    }));
  }, [categorySelectItem, yearList]);
  const yearSelectItem = useMemo(() => yearList.find(item => getYearOptionValue(item) === yearSelectValue), [yearList, yearSelectValue]);
  const scheduleList = useMemo(() => {
    return yearSelectItem?.regular_maintenance_schedules || [];
  }, [yearSelectItem]);
  const scheduleSelectOptions = useMemo<SelectOption[]>(() => {
    return scheduleList.map(item => ({
      label: getRangeSelectOptionLabel(item),
      value: getRangeSelectOptionValue(item)
    }));
  }, [scheduleList]);
  const scheduleSelectItem = useMemo(() => scheduleList.find(item => getRangeSelectOptionValue(item) === scheduleSelectValue),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [scheduleSelectValue]);
  const firstSelect = useMemo<MaintenanceListSelectType>(() => ({
    name: SELECTS_DATA[0].name,
    placeholder: SELECTS_DATA[0].placeholder,
    options: categorySelectOptions
  }), [categorySelectOptions]);
  const secondSelect = useMemo<MaintenanceListSelectType>(() => ({
    name: SELECTS_DATA[1].name,
    placeholder: SELECTS_DATA[1].placeholder,
    options: modelSelectOptions
  }), [modelSelectOptions]);
  const thirdSelect = useMemo<MaintenanceListSelectType>(() => ({
    name: SELECTS_DATA[2].name,
    placeholder: SELECTS_DATA[2].placeholder,
    options: yearSelectOptions
  }), [yearSelectOptions]);
  const fourthSelect = useMemo<MaintenanceListSelectType>(() => ({
    name: SELECTS_DATA[3].name,
    placeholder: SELECTS_DATA[3].placeholder,
    options: scheduleSelectOptions
  }), [scheduleSelectOptions]);
  const resultTitle = useMemo(() => {
    if (!yearSelectItem) return '';
    return `${yearSelectItem?.vehicle_name} เครื่องยนต์ รุ่น ${yearSelectItem?.engine_model}`;
  }, [yearSelectItem]);
  const handleOnSelectOption: MaintenanceListSelectsProps['onSelectOption'] = (updateIndex: number, updateValue: string) => {
    const updateResults = selectOptionValues.map((selectValue, selectValueIndex) => {
      if (updateIndex === selectValueIndex) return updateValue;
      return updateIndex < selectValueIndex ? '' : selectValue;
    });
    setSelectOptionValues(updateResults);
  };
  const getMaintenanceUrl = (selectItem: UseMaintenanceList['selectItem'], selectSchedule: UseMaintenanceList['selectSchedule']) => {
    if (!selectItem || !selectSchedule) {
      return '';
    }
    const scheduleValue = getRangeSelectOptionValue(selectSchedule);
    const urlBase = `${MAINTENANCE_LIST_PATH}/${getPageUrlFormat(selectItem)}`;
    return `${urlBase}${scheduleValue ? `?schedule=${scheduleValue}` : ''}`;
  };
  useEffect(() => {
    if (scheduleSelectValue) return;
    if (yearSelectOptions.length === 1) {
      handleOnSelectOption(2, yearSelectOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearSelectOptions]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scheduleParam = params?.get('schedule');
    if (!!scheduleParam) {
      setSelectOptionValues([categorySelectValue, modelSelectValue, yearSelectValue, scheduleParam]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getVehicleImageByUrl = (url: string) => {
    if (!yearSelectItem || url !== getPageUrlFormat(yearSelectItem)) return DEFAULT_VEHICLE_IMAGE;
    const item = yearSelectItem;
    const model = `${CATEGORY_TO_NAME[getCategoryOptionValueAndLabel(item)]}_${getModelOptionValue(item)}`;
    return MODEL_IMAGE_MAP[model] ?? DEFAULT_VEHICLE_IMAGE;
  };
  return {
    vehicleImageSrc: url ? getVehicleImageByUrl(url) : DEFAULT_VEHICLE_IMAGE,
    selectOptionValues,
    searchOptions: [firstSelect, secondSelect, thirdSelect, fourthSelect],
    selectItem: yearSelectItem,
    selectSchedule: scheduleSelectItem,
    resultTitle,
    handleOnSelectOption,
    getMaintenanceUrl,
    isEuro5: yearSelectItem?.european_emission_standard.includes('5')
  };
};